import dayjs from "dayjs";
import { useUpdateEffect } from "react-use";
import {
	usePatchObservationFormInstance,
	useCreateObservationFormInstance,
	useFormInstance
} from "src/hooks/useObservationForm";
import { useForm, useWatch } from "src/libs/form";
import { useNavigate } from "react-router-dom";
import useDeleteObservationFormInstance from "src/hooks/useObservationForm/useDeleteFormInstance/useDeleteFormInstance";
import { useQueryClient } from "@tanstack/react-query";
import usePreviousCompletedObservations from "src/hooks/usePreviousCompletedObservations/usePreviousCompletedObservations";
import { checkObservationFormType } from "src/utils/checkObservationFormType";
import { toast } from "react-toastify";
import { useEffect } from "react";

interface Options {
	formId: number;
	questions?: ObservationQuestion[];
	observationId?: number;
	observationType: IObservationType;
	storeUser?: Partial<StoreUser>;
	store?: Partial<Store>;
	visibleQuestions?: ObservationQuestion[];
}

const useObservationForm = ({
	questions,
	formId,
	observationId,
	observationType,
	storeUser,
	store,
	visibleQuestions
}: Options) => {
	const queryClient = useQueryClient();
	const navigate = useNavigate();

	const { mutate: createFormInstance } = useCreateObservationFormInstance();
	const {
		mutate: completeObservationForm,
		isError: hasCompleteObservationError,
		isLoading: isCompleteObservationLoading,
		isSuccess: isCompleteObservationSuccess
	} = usePatchObservationFormInstance();

	const { mutate: deleteFormInstance, isSuccess: isDeleteFormInstanceSuccess } =
		useDeleteObservationFormInstance();

	const { data: formInstance } = useFormInstance(observationId, {
		enabled:
			Boolean(observationId) &&
			observationId?.toString() !== "create-observation" // Janky because it can be a string when its a new observation
	});

	const DEFAULT_VALUES: Partial<ObservationFormState> = {
		questions,
		selectedSearchItem: { name: "" },
		isDisabled: false,
		formInstanceId: null,
		formId,
		previousAnswerIds: [],
		previousAnswers: [],
		submittedAnswers: [],
		isSubmittingAnswer: [],
		observationType
	};

	const methods = useForm<ObservationFormState>({
		mode: "onTouched",
		reValidateMode: "onChange",
		defaultValues: DEFAULT_VALUES
	});

	const { control, handleSubmit, setValue } = methods;

	const { formInstanceId } = useWatch({ control });

	const previousObservationsCompleted = usePreviousCompletedObservations(
		{ observationInstanceId: formInstanceId },
		{ enabled: Boolean(formInstanceId) }
	);

	const selectedSearchItem = useWatch({ control, name: "selectedSearchItem" });

	const markFormAsComplete = () => {
		if (!formInstanceId) return;

		const date = dayjs();

		completeObservationForm({
			id: formInstanceId,
			complete: true,
			completed_at: date.format("YYYY-MM-DDTHH:mm:ssZ"),
			month: date.month() + 1,
			year: date.year(),
			visible_questions: visibleQuestions?.map((question) => question.id)
		});
	};

	const quitObservation = () => {
		if (!formInstanceId) return;

		deleteFormInstance(formInstanceId);

		// refetch list of previous observations after deleting form instance
		queryClient.invalidateQueries(["previousObservationForm"]);

		// Intentionally no navigate here — ObservationForm renders the QuitForm
		// confirmation UI once `isQuit` + delete success are both true. The
		// QuitForm buttons handle onward navigation themselves.
	};

	const onValid = markFormAsComplete;

	const onInvalid = () => {
		window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
	};

	const submitObversation = handleSubmit(onValid, onInvalid);

	useUpdateEffect(() => {
		if (!selectedSearchItem.id) return;

		if (
			observationId === undefined ||
			String(observationId) !== "create-observation"
		)
			return;

		createFormInstance(
			{
				observationform: formId,
				created_for:
					checkObservationFormType(observationType) === "user"
						? String(selectedSearchItem.id)
						: null,
				store:
					checkObservationFormType(observationType) === "store"
						? Number(selectedSearchItem.id)
						: null,
				month: new Date().getMonth() + 1,
				year: new Date().getFullYear()
			},
			{
				onSuccess: ({ id }) => {
					navigate(
						checkObservationFormType(observationType) === "user"
							? `/observations/submissions/${formId}/user/${selectedSearchItem.id}/${id}`
							: `/observations/submissions/${formId}/store/${selectedSearchItem.id}/${id}`,
						{
							replace: true
						}
					);
				}
			}
		);
	}, [selectedSearchItem, observationId]);

	useUpdateEffect(() => {
		setValue("observationType", observationType);
	}, [observationType]);

	useEffect(() => {
		if (storeUser && checkObservationFormType(observationType) === "user")
			setValue("selectedSearchItem", storeUser);

		if (store && checkObservationFormType(observationType) === "store")
			setValue("selectedSearchItem", store);
	}, [storeUser, store]);

	useUpdateEffect(() => {
		if (formInstance?.complete) {
			toast.error(
				"This observation has already been completed. Go back and create a new observation"
			);
			navigate(`/observations/submissions/${formId}`);
		}

		if (formInstance && formInstance.id) {
			setValue("formInstanceId", formInstance.id);

			formInstance.observationinstance_response.forEach((response) => {
				if (response.observationquestion.question_type === "IMAGE_UPLOAD") {
					if (!response.images || response.images.length === 0) return;
					setValue(
						`questions.${response.observationquestion.order - 1}.images`,
						response.images,
						{
							shouldValidate: true
						}
					);
				} else {
					if (!response.value) return;

					setValue(
						`questions.${response.observationquestion.order - 1}.givenAnswer`,
						response.value,
						{
							shouldValidate: true
						}
					);
				}

				if (!response.action) return;

				setValue(
					`questions.${response.observationquestion.order - 1}.action`,
					response.action,
					{
						shouldValidate: true
					}
				);
			});
		}
	}, [formInstance, isDeleteFormInstanceSuccess]);

	return {
		methods,
		previousObservationsCompleted,
		submitObversation,
		markFormAsComplete,
		quitObservation,
		isDeleteFormInstanceSuccess,
		hasCompleteObservationError,
		isCompleteObservationLoading,
		isCompleteObservationSuccess
	};
};

export default useObservationForm;
